import { useState } from "react";
import { Send, Download, Copy, Eye, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api, { API_BASE } from "@/lib/api";
import { money } from "@/lib/format";
import { getApiErrorMessage } from "@/lib/apiError";

export default function StepReview({ quote, customer, savedId, onSave, settings, update, previewTotals }) {
    const [shareToken, setShareToken] = useState(quote.share_token || null);
    const [busy, setBusy] = useState(false);

    const totals = previewTotals || quote.totals || {};
    const isComplete = customer && quote.home?.model_name && (quote.base_price || 0) > 0;

    const enableShare = async () => {
        if (!savedId) {
            const saved = await onSave(true);
            if (!saved) return;
        }
        setBusy(true);
        try {
            const res = await api.post(`/quotes/${savedId}/share`);
            setShareToken(res.data.share_token);
            toast.success("Shareable link enabled");
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Could not enable share"));
        } finally {
            setBusy(false);
        }
    };

    const copyShareLink = () => {
        if (!shareToken) return;
        const url = `${window.location.origin}/quote/${shareToken}`;
        navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
    };

    const downloadPdf = async () => {
        let quoteId = savedId;
        const saved = await onSave(true);
        if (!saved) return;
        quoteId = saved.id || quoteId;
        try {
            const res = await api.get(`/quotes/${quoteId}/pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${quote.quote_number || "quote"}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Could not download PDF"));
        }
    };

    const markSent = async () => {
        if (!savedId) return;
        await api.post(`/quotes/${savedId}/status`, { status: "sent" });
        toast.success("Quote marked as sent");
    };

    return (
        <div className="space-y-4" data-testid="step-review">
            <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Review summary</div>
                        <div className="text-xs text-slate-500">Confirm everything looks right before sharing with the customer.</div>
                    </div>
                    {isComplete ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-[rgba(22,122,74,0.10)] text-[#167A4A] border border-[rgba(22,122,74,0.25)]">Ready</span>
                    ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Incomplete</span>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Customer</div>
                        <div className="mt-1 text-sm text-slate-900">
                            {customer ? `${customer.first_name} ${customer.last_name}` : <span className="text-amber-700">Not selected</span>}
                        </div>
                        {customer && (
                            <div className="text-xs text-slate-500 mt-1">{customer.phone || "—"} · {customer.email || "—"}</div>
                        )}
                    </div>
                    <div className="rounded-lg border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Home</div>
                        <div className="mt-1 text-sm text-slate-900">
                            {quote.home?.model_name || <span className="text-amber-700">Not selected</span>}
                        </div>
                        {quote.home?.model_name && (
                            <div className="text-xs text-slate-500 mt-1">{quote.home.manufacturer} · {quote.home.series}</div>
                        )}
                    </div>
                    <div className="rounded-lg border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Estimated total</div>
                        <div className="mt-1 text-2xl font-semibold tabular text-slate-900">{money(totals.grand_total || 0)}</div>
                        <div className="text-xs text-slate-500">Tax: {money(totals.sales_tax || 0)} · Subtotal: {money(totals.subtotal || 0)}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-4">
                        <div className="text-xs uppercase tracking-wide text-slate-500">Internal target</div>
                        <div className="mt-1 text-2xl font-semibold tabular text-slate-900">{money(totals.net_take_home || 0)}</div>
                        <div className="text-xs">
                            {totals.target_met ? (
                                <span className="text-[#167A4A]">✓ Net take home above floor</span>
                            ) : (
                                <span className="text-amber-700">Below ${(totals.take_home_floor || 0).toLocaleString()} floor</span>
                            )}
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="rounded-xl border-slate-200 p-5 space-y-4">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Generate & share</div>
                    <div className="text-xs text-slate-500">Save will create the quote if it doesn't exist yet.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button onClick={() => onSave()} variant="outline" data-testid="step-review-save-button">
                        <FileText className="mr-2 h-4 w-4" /> Save quote
                    </Button>
                    <Button onClick={downloadPdf} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" disabled={!isComplete} data-testid="step-review-download-pdf-button">
                        <Download className="mr-2 h-4 w-4" /> Download PDF
                    </Button>
                    {!shareToken ? (
                        <Button onClick={enableShare} variant="outline" disabled={busy || !isComplete} data-testid="step-review-enable-share-button">
                            <Eye className="mr-2 h-4 w-4" /> Enable share link
                        </Button>
                    ) : (
                        <>
                            <Button onClick={copyShareLink} variant="outline" data-testid="step-review-copy-share-button">
                                <Copy className="mr-2 h-4 w-4" /> Copy share link
                            </Button>
                            <Button asChild variant="outline">
                                <a href={`/quote/${shareToken}`} target="_blank" rel="noreferrer" data-testid="step-review-open-share-button">
                                    <ExternalLink className="mr-2 h-4 w-4" /> Open customer view
                                </a>
                            </Button>
                        </>
                    )}
                    <Button onClick={markSent} variant="outline" disabled={!savedId} data-testid="step-review-mark-sent-button">
                        <Send className="mr-2 h-4 w-4" /> Mark as sent
                    </Button>
                </div>
                {shareToken && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 break-all" data-testid="step-review-share-link">
                        {window.location.origin}/quote/{shareToken}
                    </div>
                )}
            </Card>
        </div>
    );
}
